/**
 * The ladder. Everything here is a pure function of the catalogue, a child's
 * Can Do state, and an append-only event list. No React, no storage, no clock —
 * callers pass `today` explicitly.
 *
 * Events: { id, skillId, type, at: 'YYYY-MM-DD', by: 'kid'|'parent', withHelp?, note?, learnerId? }
 *   introduced — the steps were opened (kid or parent)
 *   practiced  — a practice attempt; withHelp says whether a grown-up helped
 *   verified   — a parent says: independent. Only counts when by === 'parent'.
 *   taught     — a parent says: they taught someone. Only counts when by === 'parent'.
 *   reset      — a parent starts the ladder over (history kept; state derives after it)
 */

export const STATES = Object.freeze([
  'not_started', 'learning', 'with_help', 'mostly', 'independent', 'can_teach',
]);

export const STATE_LABELS = Object.freeze({
  not_started: 'Not started',
  learning: 'Learning',
  with_help: 'With help',
  mostly: 'Mostly on my own',
  independent: 'Independent',
  can_teach: 'Can teach it',
});

/** Solo practices a kid needs before they can claim "mostly on my own". */
export const SOLO_FOR_MOSTLY = 3;

export const SAFETY = Object.freeze(['free', 'with_adult', 'adult_enables']);

/** A child's Can Do state, as stored under profile.apps['can-do']. */
export const EMPTY_APP_STATE = Object.freeze({
  enabled: [],
  adultEnabled: [],
  privileges: {},
  wishes: [],
  events: [],
});

const isParent = (e) => e.by === 'parent';

/** Events for one skill, in order, after the most recent parent reset. */
export function eventsFor(events, skillId) {
  const mine = (events ?? []).filter((e) => e.skillId === skillId);
  let lastReset = -1;
  mine.forEach((e, i) => { if (e.type === 'reset' && isParent(e)) lastReset = i; });
  return mine.slice(lastReset + 1);
}

export function progressOf(events, skillId) {
  const list = eventsFor(events, skillId);
  const practices = list.filter((e) => e.type === 'practiced');
  const solo = practices.filter((e) => !e.withHelp).length;
  const withHelp = practices.length - solo;
  const verified = list.find((e) => e.type === 'verified' && isParent(e));
  const taught = verified ? list.find((e) => e.type === 'taught' && isParent(e)) : undefined;
  const last = list[list.length - 1];

  let state = 'not_started';
  if (taught) state = 'can_teach';
  else if (verified) state = 'independent';
  else if (solo >= SOLO_FOR_MOSTLY) state = 'mostly';
  else if (practices.length >= 2) state = 'with_help';
  else if (list.length > 0) state = 'learning';

  return {
    state,
    practices: practices.length,
    solo,
    withHelp,
    verifiedAt: verified?.at ?? null,
    taughtAt: taught?.at ?? null,
    lastAt: last?.at ?? null,
    started: list.length > 0,
  };
}

export function stateOf(events, skillId) {
  return progressOf(events, skillId).state;
}

export const isOwned = (state) => state === 'independent' || state === 'can_teach';

/**
 * The skills a child can see: on their list, and — for adult_enables skills —
 * only if a parent turned that skill on for this child. Enforced here so a UI
 * that forgets cannot show a knife lesson.
 */
export function visibleSkills(catalogue, app) {
  const enabled = new Set(app?.enabled ?? []);
  const adult = new Set(app?.adultEnabled ?? []);
  return catalogue.filter((s) =>
    enabled.has(s.id) && (s.safety !== 'adult_enables' || adult.has(s.id)));
}

/** Prereqs met means every prerequisite is Independent or better. */
export function prereqsMet(skill, events) {
  return (skill.prereqs ?? []).every((id) => isOwned(stateOf(events, id)));
}

/** Not-started, visible, prereqs met — the "Next up" row. */
export function suggestNext(catalogue, app, events, limit = 3) {
  return visibleSkills(catalogue, app)
    .filter((s) => !progressOf(events, s.id).started && prereqsMet(s, events))
    .slice(0, limit);
}

/** Skills at "mostly" — the parent's verification queue. */
export function awaitingVerification(catalogue, app, events) {
  return visibleSkills(catalogue, app)
    .filter((s) => stateOf(events, s.id) === 'mostly');
}

/** Every skill this child owns — the kid-facing "things I can do" count. */
export function ownedCount(catalogue, events) {
  return catalogue.filter((s) => isOwned(stateOf(events, s.id))).length;
}

/**
 * Things a parent no longer has to do for this child. The week-12 number.
 * A skill with `relief: false` (stamped from its branch by the catalogue) is
 * owned but never counted here: making an apology was never the parent's job.
 */
export function noLongerHaveTo(catalogue, events) {
  return catalogue.filter((s) => s.relief !== false && isOwned(stateOf(events, s.id))).length;
}

/** The default list for a child of this age: typical range contains the age. */
export function suggestedForAge(catalogue, age) {
  return catalogue
    .filter((s) => age >= s.typicalAge[0] && age <= s.typicalAge[1])
    .map((s) => s.id);
}

/** Group any list of skills by branch id, preserving catalogue order. */
export function byBranch(skills) {
  const out = new Map();
  for (const s of skills) {
    if (!out.has(s.branch)) out.set(s.branch, []);
    out.get(s.branch).push(s);
  }
  return out;
}

/** Normalise an app-state bag from storage. Never throws. */
export function normalizeAppState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const strs = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
  const events = Array.isArray(r.events)
    ? r.events.filter((e) => e && typeof e === 'object' && typeof e.skillId === 'string' && typeof e.type === 'string')
    : [];
  return {
    enabled: strs(r.enabled),
    adultEnabled: strs(r.adultEnabled),
    privileges: r.privileges && typeof r.privileges === 'object' ? { ...r.privileges } : {},
    wishes: strs(r.wishes),
    events,
  };
}
